import {
    GOOGLE_METADATA_BY_KEY,
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import styled, { useTheme } from 'styled-components';
import BaseGridScroll from './GridScroll';
import BaseTileWrapper from './Tile';
import GoogleTikTokSparkline from './DataVis/GoogleTikTokSparkline';
import Link from './Link';
import MetricStat from './OverviewCard/MetricStat';
import { ParentSize } from '@visx/responsive';
import SemanticList from './SemanticList';
import Sparkline from './DataVis/Sparkline';
import Stack from './Stack';
import StackedAreaSparkline from './DataVis/StackedAreaSparkline';
import { formatGrowth } from 'utils/helpers';
import isFinite from 'lodash/isFinite';

const TILE_HEIGHT = '5.9375em;';

const TileWrapper = styled(Link)`
    ${BaseTileWrapper.Fragment};
    padding: 0;
    min-height: ${TILE_HEIGHT};
    justify-content: space-between;
    overflow: hidden;
`;

const OverviewTileWrapper = styled(TileWrapper)`
    min-height: 7.75em;
`;

const Stats = styled(Stack)`
    padding: 12px 14px 0;
    position: relative;
`;

const Title = styled.span`
    font-weight: 500;
    text-transform: lowercase;
    line-height: 1.2em;

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;

const YoYWrapper = styled.span`
    font-size: 12px;
    line-height: 1em;
    color: ${({ theme }) => theme.gray_3};
    position: absolute;
    margin-top: 0.625em;
`;

const SparklineWrapper = styled.div`
    margin-left: -1px;
    margin-right: -1px;
`;

const strokeThemeColorAccessors = [
    ({ theme }) => theme.gray_5,
    ({ theme }) => theme.purple_1,
];

function Tile({ title, data, YoY, ...props }) {
    return (
        <TileWrapper {...props}>
            <Stats>
                <Title>
                    {title || (['success'].includes(props.status) ? '-' : null)}
                </Title>
                <YoYWrapper>
                    {isFinite(YoY) ||
                    ['error', 'success'].includes(props.status) ? (
                        <span>{formatGrowth(YoY)} YoY</span>
                    ) : null}
                </YoYWrapper>
            </Stats>
            {data ? (
                <SparklineWrapper>
                    <ParentSize>
                        {({ width }) => (
                            <Sparkline
                                data={data}
                                width={width}
                                height={60}
                                strokeThemeColorAccessors={
                                    strokeThemeColorAccessors
                                }
                            />
                        )}
                    </ParentSize>
                </SparklineWrapper>
            ) : null}
        </TileWrapper>
    );
}

function OverviewTile({
    title,
    monthlyGoogleTimeSeries,
    weeklyGoogleTimeSeries,
    weeklyTikTokTimeSeries,
    YoY,
    non_cum_YoY_rolling,
    ...props
}) {
    return (
        <OverviewTileWrapper {...props}>
            <Stats>
                <Title>
                    {title || (['success'].includes(props.status) ? '-' : null)}
                </Title>
                <YoYWrapper
                    css={`
                        display: flex;
                        gap: 0.125rem;
                    `}
                >
                    {isFinite(YoY) ||
                    ['error', 'success'].includes(props.status) ? (
                        <MetricStat
                            dataSource="GOOGLE"
                            metric={GOOGLE_METADATA_BY_KEY.YoY.format(YoY)}
                            metricLabel={
                                GOOGLE_METADATA_BY_KEY.YoY.tableHeadColumnLabel
                            }
                        />
                    ) : null}
                    {isFinite(non_cum_YoY_rolling) ||
                    ['error', 'success'].includes(props.status) ? (
                        <MetricStat
                            dataSource="TIKTOK"
                            metric={TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling.format(
                                non_cum_YoY_rolling,
                            )}
                            metricLabel={
                                TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling
                                    .tableHeadColumnLabel
                            }
                        />
                    ) : null}
                </YoYWrapper>
            </Stats>
            {weeklyTikTokTimeSeries ? (
                <SparklineWrapper>
                    <ParentSize>
                        {({ width }) => (
                            <GoogleTikTokSparkline
                                monthlyGoogleTimeSeries={
                                    monthlyGoogleTimeSeries
                                }
                                weeklyGoogleTimeSeries={weeklyGoogleTimeSeries}
                                weeklyTikTokTimeSeries={weeklyTikTokTimeSeries}
                                width={width}
                                height={60}
                            />
                        )}
                    </ParentSize>
                </SparklineWrapper>
            ) : null}
        </OverviewTileWrapper>
    );
}

function OverviewTileV2({
    title,
    timeSeriesGoogle,
    timeSeriesTikTok,
    timeSeriesInstagram,
    YoY,
    ...props
}) {
    const theme = useTheme();
    const series = [];
    if (timeSeriesGoogle)
        series.push({
            id: 'google',
            timeSeries: timeSeriesGoogle,
            color: theme.google_blue,
        });
    if (timeSeriesTikTok)
        series.push({
            id: 'tiktok',
            timeSeries: timeSeriesTikTok,
            color: theme.tiktok_pink,
        });
    if (timeSeriesInstagram)
        series.push({
            id: 'instagram',
            timeSeries: timeSeriesInstagram,
            color: theme.instagram_orange,
        });
    return (
        <OverviewTileWrapper {...props}>
            <Stats>
                <Title>
                    {title || (['success'].includes(props.status) ? '-' : null)}
                </Title>
                <YoYWrapper
                    css={`
                        display: flex;
                        gap: 0.125rem;
                    `}
                >
                    {isFinite(YoY) ||
                    ['error', 'success'].includes(props.status) ? (
                        <MetricStat
                            metric={POPULARITY_METADATA_BY_KEY.YoY.format(YoY)}
                            metricLabel={
                                POPULARITY_METADATA_BY_KEY.YoY
                                    .tableHeadColumnLabel
                            }
                        />
                    ) : null}
                </YoYWrapper>
            </Stats>
            {series.length || ['success'].includes(props.status) ? (
                <SparklineWrapper>
                    <ParentSize>
                        {({ width }) => (
                            <StackedAreaSparkline
                                series={series}
                                width={width}
                                height={60}
                            />
                        )}
                    </ParentSize>
                </SparklineWrapper>
            ) : null}
        </OverviewTileWrapper>
    );
}

const TILE_WIDTH = 160;

const GridWrap = styled(SemanticList)`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${TILE_WIDTH}px, 1fr));
    grid-row-gap: ${({ theme }) => theme.space_9}px;
    grid-column-gap: ${({ theme }) => theme.space_9}px;

    & > li {
        display: inline-flex;
    }
`;

const GridScrollPadding = 18;

const GridScroll = styled(BaseGridScroll).attrs(
    ({ padding = GridScrollPadding, paddingTop = 0 }) => ({
        padding,
        paddingTop,
    }),
)`
    & > li {
        flex: 0 0
            calc(25% - ${GridScrollPadding}px + ${GridScrollPadding}px / 4);
    }

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        & > li {
            min-width: ${TILE_WIDTH}px;
        }
    }
`;

const Message = styled.div`
    color: ${({ theme }) => theme.gray_2};
    font-weight: 500;
    min-height: ${TILE_HEIGHT};
    display: flex;
    justify-content: center;
    align-items: center;
`;

function LinkTile({ as = Link, mixpanelContext = 'SparkTile', ...props }) {
    return <Tile as={as} mixpanelContext={mixpanelContext} {...props} />;
}

function Tiles({
    tiles,
    status,
    Grid = GridWrap,
    Tile = LinkTile,
    emptyMessage = 'No trends available.',
    ...props
}) {
    if (tiles && tiles.length) {
        return (
            <Grid {...props}>
                {tiles.map(({ key, ...tile }) => (
                    <Tile mixpanelContext="SparkTile" key={key} {...tile} />
                ))}
            </Grid>
        );
    }
    if (['loading', 'idle'].includes(status)) {
        return (
            <Grid>
                <Tile as="span" isDisabled={true} isLoading={true} />
            </Grid>
        );
    }
    if (status === 'error') {
        return <Message>Failed to load trends</Message>;
    }
    if (tiles.length < 1) {
        return <Message>{emptyMessage}</Message>;
    }

    return null;
}

const SparkTiles = {
    Tile,
    GridWrap,
    GridScroll,
    Tiles,
    OverviewTile,
    OverviewTileV2,
};

export default SparkTiles;
